import React, { useMemo, useState, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
	createEditor,
	Editor,
	Transforms,
	Node,
	Element as SlateElement,
	Point,
	Range,
	Text
} from 'slate';
import {
	Slate,
	Editable,
	withReact,
	useSlate,
	useSelected,
	useFocused,
	useSlateStatic
} from 'slate-react';
import ImageIcon from '@material-ui/icons/Image';
import imageExtensions from 'image-extensions';
import escapeHtml from 'escape-html';
import { jsx } from 'slate-hyperscript';
import isUrl from 'is-url';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import isHotkey from 'is-hotkey';
import FormatBoldIcon from '@material-ui/icons/FormatBold';
import FormatItalicIcon from '@material-ui/icons/FormatItalic';
import FormatUnderlinedIcon from '@material-ui/icons/FormatUnderlined';
import Filter1Icon from '@material-ui/icons/Filter1';
import Filter2Icon from '@material-ui/icons/Filter2';
import FormatListNumberedIcon from '@material-ui/icons/FormatListNumbered';
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted';
import LinkIcon from '@material-ui/icons/Link';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import { makeStyles } from '@material-ui/styles';
import { Button, CircularProgress } from '@material-ui/core';
import { showToast } from '../actions';
import StatusBullet from './StatusBullet';

export const editorStyles = {
	'& h1, & h2': {
		fontWeight: 'bold',
		lineHeight: '100%'
	},
	'& h1': {
		fontSize: '2em'
	},
	'& h2': {
		fontSize: '1.5em'
	},
	'& ol': {
		listStyle: 'decimal'
	},
	'& ul': {
		listStyle: 'disc'
	},
	'& a': {
		color: '#0000EE'
	}
};

const useStyles = makeStyles(() => ({
	toolbar: {
		width: '100%',
		padding: '10px 5px',
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		borderBottom: '1px solid #eeeeee'
	},
	editorWrap: {
		padding: '10px',
		...editorStyles
	},
	editor: {
		maxHeight: '500px',
		overflowY: 'auto'
	},
	btn: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginRight: '12px',
		cursor: 'pointer',
		fontSize: '12px',
		borderRadius: '4px',
		padding: '2px'
	},
	btnActive: {
		backgroundColor: '#eeeeee'
	},
	wrapperColors: {
		display: 'flex',
		alignItems: 'center'
	},
	colorBlock: {
		width: '20px',
		height: '20px',
		border: '1px solid #000',
		borderRadius: '2px',
		marginRight: '6px'
	},
	colorList: {
		display: 'flex',
		alignItems: 'center',
		overflow: 'hidden',
		transition: 'all 0.3s ease'
	},
	bulletWrap: {
		cursor: 'pointer',
		marginRight: '6px',
		height: '20px'
	},
	noMargins: {
		margin: '0'
	},
	image: {
		display: 'block',
		maxWidth: '100%',
		maxHeight: '20em'
	},
	tableToolbar: {
		display: 'flex',
		alignItems: 'center',
		marginLeft: '8px',
		paddingLeft: '8px',
		borderLeft: '1px solid #eeeeee'
	},
	tableBtn: {
		fontSize: '11px',
		padding: '2px 6px',
		marginRight: '4px',
		cursor: 'pointer',
		borderRadius: '4px',
		whiteSpace: 'nowrap',
		userSelect: 'none'
	}
}));

const HOTKEYS = {
	'mod+b': 'bold',
	'mod+i': 'italic',
	'mod+u': 'underline'
};

const LIST_TYPES = ['numbered-list', 'bulleted-list'];

const colors = [
	{ type: 'primary', color: '#3949ab' },
	{ type: 'info', color: '#039be5' },
	{ type: 'success', color: '#66bb6a' },
	{ type: 'warning', color: '#ffa726' },
	{ type: 'error', color: '#f44336' },
	{ type: 'settled', color: '#ffeb3b' },
	{ type: 'onHold', color: '#9c27b0' },
	{ type: 'black', color: '#212121' },
	{ type: 'transferred', color: '#00bcd4' }
];

// Inline styles rather than classes: tables must look the same in the editor and
// in the read-only RenderCommentWithStyle view, which doesn't apply editorStyles.
// Auto-size to content — `width: 100%` + `tableLayout: fixed` stretched every
// pasted table across the commentary box (UAT: "columns are super wide").
const tableWrapStyle = {
	borderCollapse: 'collapse',
	width: 'auto',
	maxWidth: '100%',
	margin: '8px 0'
};

const tableCellStyle = {
	border: '1px solid #cccccc',
	padding: '4px 8px',
	textAlign: 'left',
	verticalAlign: 'top',
	wordBreak: 'break-word',
	whiteSpace: 'pre-wrap',
	cursor: 'text'
};

const tableHeaderCellStyle = {
	...tableCellStyle,
	fontWeight: 'bold',
	backgroundColor: '#f5f5f5'
};

export function Element(props) {
	const { attributes, children, element } = props;
	switch (element.type) {
		case 'block-quote':
			return <blockquote {...attributes}>{children}</blockquote>;
		case 'bulleted-list':
			return (
				<ul
					style={{ paddingLeft: '20px' }}
					{...attributes}
				>
					{children}
				</ul>
			);
		case 'numbered-list':
			return (
				<ol
					style={{ paddingLeft: '20px' }}
					{...attributes}
				>
					{children}
				</ol>
			);
		case 'list-item':
			return <li {...attributes}>{children}</li>;
		case 'heading-one':
			return <h1 {...attributes}>{children}</h1>;
		case 'heading-two':
			return <h2 {...attributes}>{children}</h2>;
		case 'link':
			return (
				<a
					{...attributes}
					href={element.url}
					target="_blank"
					rel="noopener noreferrer"
				>
					{children}
				</a>
			);
		case 'image':
			return <Image {...props} />;
		case 'table':
			// Slate's attributes go on the direct parent of `children`, so they sit on
			// <tbody> rather than <table>.
			return (
				<table style={tableWrapStyle}>
					<tbody {...attributes}>{children}</tbody>
				</table>
			);
		case 'table-row':
			return <tr {...attributes}>{children}</tr>;
		case 'table-cell':
			return element.header ? (
				<th
					style={tableHeaderCellStyle}
					{...attributes}
				>
					{children}
				</th>
			) : (
				<td
					style={tableCellStyle}
					{...attributes}
				>
					{children}
				</td>
			);
		default:
			return <p {...attributes}>{children}</p>;
	}
}

export function Leaf({ attributes, children, leaf }) {
	if (leaf.bold) {
		// eslint-disable-next-line
		children = <strong>{children}</strong>;
	}
	if (leaf.italic) {
		// eslint-disable-next-line
		children = <em>{children}</em>;
	}
	if (leaf.underline) {
		// eslint-disable-next-line
		children = <u>{children}</u>;
	}
	return (
		<span
			{...attributes}
			style={{ color: `${leaf.color && leaf.color}` }}
		>
			{children}
		</span>
	);
}

const ELEM_TYPES = [
	{ link: 'A' },
	{ quote: 'BLOCKQUOTE' },
	{ paragraph: 'P' },
	{ image: 'IMG' },
	{ 'heading-one': 'H1' },
	{ 'heading-two': 'H2' },
	{ 'heading-three': 'H3' },
	{ 'heading-four': 'H4' },
	{ 'heading-five': 'H5' },
	{ 'heading-six': 'H6' },
	{ 'list-item': 'LI' },
	{ 'numbered-list': 'OL' },
	{ 'bulleted-list': 'UL' }
];

const ELEMENT_TAGS = {
	A: (el) => ({ type: 'link', url: el.getAttribute('href') }),
	BLOCKQUOTE: () => ({ type: 'quote' }),
	P: () => ({ type: 'paragraph' }),
	IMG: (el) => ({ type: 'image', url: el.getAttribute('src') }),
	H1: () => ({ type: 'heading-one' }),
	H2: () => ({ type: 'heading-two' }),
	H3: () => ({ type: 'heading-three' }),
	H4: () => ({ type: 'heading-four' }),
	H5: () => ({ type: 'heading-five' }),
	H6: () => ({ type: 'heading-six' }),
	LI: () => ({ type: 'list-item' }),
	OL: () => ({ type: 'numbered-list' }),
	UL: () => ({ type: 'bulleted-list' })
};

const TEXT_TAGS = {
	DEL: () => ({ strikethrough: true }),
	EM: () => ({ italic: true }),
	I: () => ({ italic: true }),
	S: () => ({ strikethrough: true }),
	STRONG: () => ({ bold: true }),
	B: () => ({ bold: true }),
	U: () => ({ underline: true }),
	SPAN: (el) => (el?.style?.color ? { color: el?.style?.color } : {})
};

const isMarkActive = (editor, format) => {
	const marks = Editor.marks(editor);
	return marks ? marks[format] === true : false;
};

const isColorActive = (editor, format) => {
	const marks = Editor.marks(editor);
	return marks ? !!(marks.color === format) : false;
};

const isBlockActive = (editor, format) => {
	const [match] = Editor.nodes(editor, {
		match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format
	});

	return !!match;
};

const toggleMark = (editor, format) => {
	const isActive = isMarkActive(editor, format);

	if (isActive) {
		Editor.removeMark(editor, format);
	} else if (format.startsWith('#')) {
		Editor.addMark(editor, 'color', format);
	} else {
		Editor.addMark(editor, format, true);
	}
};

const toggleBlock = (editor, format) => {
	// Inside a table the lowest block is the cell itself, so setNodes would turn
	// cells into paragraphs and break the table apart.
	if (isBlockActive(editor, 'table-cell')) return;

	const isActive = isBlockActive(editor, format);
	const isList = LIST_TYPES.includes(format);

	Transforms.unwrapNodes(editor, {
		match: (n) =>
			LIST_TYPES.includes(!Editor.isEditor(n) && SlateElement.isElement(n) && n.type),
		split: true
	});
	// eslint-disable-next-line
	Transforms.setNodes(editor, { type: isActive ? 'paragraph' : isList ? 'list-item' : format });

	if (!isActive && isList) {
		const block = { type: format, children: [] };
		Transforms.wrapNodes(editor, block);
	}
};

const isLinkActive = (editor) => {
	const [link] = Editor.nodes(editor, {
		match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'link'
	});
	return !!link;
};

const unwrapLink = (editor) => {
	Transforms.unwrapNodes(editor, {
		match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'link'
	});
};

const wrapLink = (editor, url) => {
	if (isLinkActive(editor)) {
		unwrapLink(editor);
	}

	const { selection } = editor;
	const isCollapsed = selection && Range.isCollapsed(selection);

	const link = {
		type: 'link',
		url,
		children: isCollapsed ? [{ text: url }] : []
	};

	if (isCollapsed) {
		Transforms.insertNodes(editor, link);
	} else {
		Transforms.wrapNodes(editor, link, { split: true });
		Transforms.collapse(editor, { edge: 'end' });
	}

	// Move cursor outside the link so subsequent typing isn't styled as a link
	const [linkEntry] = Editor.nodes(editor, {
		match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'link'
	});
	if (linkEntry) {
		const [, linkPath] = linkEntry;
		const after = Editor.after(editor, linkPath);
		if (after) {
			Transforms.select(editor, after);
		} else {
			Transforms.move(editor, { unit: 'offset' });
		}
	}
};

const withLinks = (editor) => {
	const { insertData, insertText, isInline } = editor;

	// eslint-disable-next-line
	editor.isInline = (element) => {
		return element.type === 'link' ? true : isInline(element);
	};

	// eslint-disable-next-line
	editor.insertText = (text) => {
		if (text && isUrl(text)) {
			wrapLink(editor, text);
		} else {
			insertText(text);
		}
	};

	// eslint-disable-next-line
	editor.insertData = (data) => {
		const text = data.getData('text/plain');

		if (text && isUrl(text)) {
			wrapLink(editor, text);
		} else {
			insertData(data);
		}
	};

	return editor;
};

const insertLink = (editor, url) => {
	if (editor.selection) {
		wrapLink(editor, url);
	}
};

function BlockButton({ format, icon, classes, activeStyle }) {
	const editor = useSlate();
	return (
		<div
			className={clsx(...classes, isBlockActive(editor, format) && activeStyle)}
			onMouseDown={(event) => {
				event.preventDefault();
				toggleBlock(editor, format);
			}}
		>
			{icon}
		</div>
	);
}

function MarkButton({ format, icon, classes, activeStyle }) {
	const editor = useSlate();
	return (
		<div
			className={clsx(...classes, isMarkActive(editor, format) && activeStyle)}
			onMouseDown={(event) => {
				event.preventDefault();
				toggleMark(editor, format);
			}}
		>
			{icon}
		</div>
	);
}

function ColorButton({ format, icon, classes, callback }) {
	const editor = useSlate();
	if (isColorActive(editor, format)) {
		callback(format);
	}
	return (
		<div
			className={clsx(...classes)}
			onMouseDown={(event) => {
				event.preventDefault();
				toggleMark(editor, format);
			}}
		>
			{icon}
		</div>
	);
}

function LinkButton({ type, icon, classes, activeStyle }) {
	const editor = useSlate();
	return (
		<div
			className={clsx(...classes, isLinkActive(editor) && type === 'wrap' && activeStyle)}
		onMouseDown={(event) => {
			event.preventDefault();
			if (type === 'wrap') {
				const savedSelection = editor.selection;
				const url = window.prompt('Enter the URL of the link:');
				if (!url) return;
				if (savedSelection) Transforms.select(editor, savedSelection);
				insertLink(editor, url);
			} else if (isLinkActive(editor)) {
				unwrapLink(editor);
			}
		}}
		>
			{icon}
		</div>
	);
}

export const serialize = (value) => value?.map((n) => Node.string(n)).join('\n');

export const deserialize = (string) =>
	string.split('\n').map((line) => ({
		children: [{ text: line }]
	}));

export const deserializeHtml = (el) => {
	if (el.nodeType === 3) {
		return el.textContent;
		// eslint-disable-next-line
	} else if (el.nodeType !== 1) {
		return null;
	} else if (el.nodeName === 'BR') {
		return '\n';
	}

	const { nodeName } = el;
	let parent = el;

	if (nodeName === 'PRE' && el.childNodes[0] && el.childNodes[0].nodeName === 'CODE') {
		// eslint-disable-next-line
		parent = el.childNodes[0];
	}
	const children = Array.from(parent.childNodes).map(deserializeHtml).flat();

	if (el.nodeName === 'IMG') {
		const attrs = ELEMENT_TAGS[nodeName](el);
		return jsx('element', attrs, [{ text: '' }]);
	}

	if (!children.length) {
		return jsx('element', { type: 'paragraph' }, { text: '' });
	}

	if (
		el.nodeName === 'BODY' ||
		el.nodeType === 'DIV' ||
		el.nodeType === 'SPAN' ||
		el.nodeType === 'FONT'
	) {
		return jsx('fragment', {}, children);
	}

	if (ELEMENT_TAGS[nodeName]) {
		const attrs = ELEMENT_TAGS[nodeName](el);
		return jsx('element', attrs, children);
	}

	if (TEXT_TAGS[nodeName]) {
		const attrs = TEXT_TAGS[nodeName](el);
		return children.map((child) => {
			const elem = ELEM_TYPES.find((itm) => Object.keys(itm)[0] === child?.type);
			return elem
				? jsx('element', ELEMENT_TAGS[elem[Object.keys(elem)[0]]](el), child)
				: jsx('text', attrs, child);
		});
	}

	return children;
};

export const serializeHtml = (node) => {
	if (Text.isText(node)) {
		let string = escapeHtml(node.text);
		if (node.bold) {
			string = `<strong>${string}</strong>`;
		}
		if (node.italic) {
			string = `<i>${string}</i>`;
		}
		if (node.underline) {
			string = `<u>${string}</u>`;
		}
		if (node.type === 'list-item') {
			string = `<li>${string}</li>`;
		}

		if (node.color) {
			string = `<span style="color: ${node.color}">${string}</span>`;
		}
		return string;
	}

	const children = node?.children?.map((n) => serializeHtml(n)).join('');

	switch (node.type) {
		case 'quote':
			return `<blockquote><p>${children}</p></blockquote>`;
		case 'paragraph':
			return `<p>${children}</p>`;
		case 'bulleted-list':
			return `<ul>${children}</ul>`;
		case 'numbered-list':
			return `<ol>${children}</ol>`;
		case 'list-item':
			return `<li>${children}</li>`;
		case 'heading-one':
			return `<h1>${children}</h1>`;
		case 'heading-two':
			return `<h2>${children}</h2>`;
		case 'link':
			return `<a href="${escapeHtml(node.url)}">${children}</a>`;
		case 'image':
			return `<div><img alt="" src="${node.url}" style="display: block; max-width: 100%; max-height: 20em;" />${children}</div>`;
		default:
			return children;
	}
};

function Image({ attributes, children, element }) {
	const classes = useStyles();
	const selected = useSelected();
	const focused = useFocused();

	return (
		<div {...attributes}>
			<div contentEditable={false}>
				<img
					alt=""
					src={element.url}
					style={selected && focused ? { border: '1px solid red' } : {}}
					className={classes.image}
				/>
			</div>
			{children}
		</div>
	);
}

const isImageUrl = (url) => {
	if (!url) return false;
	if (!isUrl(url)) return false;
	const ext = new URL(url).pathname.split('.').pop();
	return imageExtensions.includes(ext);
};

export const insertImage = (editor, url) => {
	const text = { text: '' };
	const image = { type: 'image', url, children: [text] };
	Transforms.insertNodes(editor, image);
};

function InsertImageButton({ onUploadImage }) {
	const editor = useSlateStatic();
	const fileInputRef = useRef(null);
	const [uploading, setUploading] = useState(false);

	const handleFileChange = async (e) => {
		const file = e.target.files[0];
		if (!file) return;
		e.target.value = '';

		if (onUploadImage) {
			setUploading(true);
			try {
				const url = await onUploadImage(file);
				if (url) insertImage(editor, url);
			} finally {
				setUploading(false);
			}
		} else {
			const reader = new FileReader();
			reader.onload = () => insertImage(editor, reader.result);
			reader.readAsDataURL(file);
		}
	};

	return (
		<>
			<input
				ref={fileInputRef}
				type="file"
				accept="image/*"
				style={{ display: 'none' }}
				onChange={handleFileChange}
			/>
			<Button
				disabled={uploading}
				onMouseDown={(event) => {
					event.preventDefault();
					fileInputRef.current?.click();
				}}
			>
				{uploading ? <CircularProgress size={16} /> : <ImageIcon />}
			</Button>
		</>
	);
}

export const withImages = (editor, uploadRef) => {
	const { insertData, isVoid } = editor;

	// eslint-disable-next-line
	editor.isVoid = (element) => {
		return element.type === 'image' ? true : isVoid(element);
	};

	// eslint-disable-next-line
	editor.insertData = (data) => {
		const text = data.getData('text/plain');
		const { files } = data;

		if (files && files.length > 0) {
			// eslint-disable-next-line no-restricted-syntax
			for (const file of files) {
				const [mime] = file.type.split('/');

				if (mime === 'image') {
					const onUpload = uploadRef?.current;
					if (onUpload) {
						onUpload(file).then((url) => {
							if (url) insertImage(editor, url);
						});
					} else {
						const reader = new FileReader();
						reader.addEventListener('load', () => insertImage(editor, reader.result));
						reader.readAsDataURL(file);
					}
				}
			}
		} else if (isImageUrl(text)) {
			insertImage(editor, text);
		} else {
			insertData(data);
		}
	};

	return editor;
};

// A pasted spreadsheet range can be hundreds of cells wide. Anything beyond this
// is unreadable in the A4 PDF anyway, so trim and tell the user.
export const MAX_TABLE_COLUMNS = 12;
export const MAX_TABLE_ROWS = 60;

// \s matches U+00A0, so a non-breaking space from Excel collapses here too.
const cellText = (el) => el.textContent.replace(/\s+/g, ' ').trim();

const isBlank = (value) => !String(value ?? '').trim();

// Excel/Sheets often copies a halo of empty rows and columns around the range.
const trimEmptyTableEdges = (rows) => {
	if (!rows.length) return rows;

	const colCount = rows.reduce((max, row) => Math.max(max, row.length), 0);
	const padded = rows.map((row) => {
		const copy = row.slice();
		while (copy.length < colCount) copy.push('');
		return copy;
	});

	const colEmpty = (i) => padded.every((row) => isBlank(row[i]));
	const rowEmpty = (row) => row.every(isBlank);

	let left = 0;
	let right = colCount - 1;
	while (left <= right && colEmpty(left)) left += 1;
	while (right >= left && colEmpty(right)) right -= 1;
	if (left > right) return [];

	let top = 0;
	let bottom = padded.length - 1;
	while (top <= bottom && rowEmpty(padded[top])) top += 1;
	while (bottom >= top && rowEmpty(padded[bottom])) bottom -= 1;

	return padded.slice(top, bottom + 1).map((row) => row.slice(left, right + 1));
};

const isTooSmallToBeATable = (rows) =>
	!rows.length || (rows.length < 2 && rows[0].length < 2);

// Structure is kept, source styling (Excel/Word fill colours, fonts, merged-cell
// attributes, nested tables, images) is dropped. This is also the sanitising
// boundary: pasted HTML is never stored or rendered, only these node types are.
const buildTable = (rows, hasHeader) => {
	const columnCount = Math.min(
		rows.reduce((max, row) => Math.max(max, row.length), 0),
		MAX_TABLE_COLUMNS
	);

	if (!columnCount) return null;

	return {
		type: 'table',
		children: rows.slice(0, MAX_TABLE_ROWS).map((row, rowIndex) => ({
			type: 'table-row',
			children: Array.from({ length: columnCount }, (_, colIndex) => ({
				type: 'table-cell',
				...(hasHeader && rowIndex === 0 ? { header: true } : {}),
				children: [{ text: row[colIndex] ?? '' }]
			}))
		}))
	};
};

const wasTrimmed = (rows) =>
	rows.length > MAX_TABLE_ROWS || rows.some((row) => row.length > MAX_TABLE_COLUMNS);

// Excel, Word, Google Sheets and web pages all put a <table> on the clipboard.
export const parseHtmlTable = (html) => {
	const parsed = new DOMParser().parseFromString(html, 'text/html');
	const table = parsed.querySelector('table');
	if (!table) return null;

	const rows = trimEmptyTableEdges(
		Array.from(table.querySelectorAll('tr'))
			// Ignore rows belonging to a nested table.
			.filter((tr) => tr.closest('table') === table)
			.map((tr) =>
				Array.from(tr.children)
					.filter((cell) => cell.nodeName === 'TD' || cell.nodeName === 'TH')
					.map(cellText)
			)
			.filter((row) => row.length)
	);

	// A lone cell is almost always meant as inline text, not a table.
	if (isTooSmallToBeATable(rows)) return null;

	const hasHeader = Array.from(table.querySelectorAll('tr'))[0]?.querySelector('th') != null;

	return { table: buildTable(rows, hasHeader), trimmed: wasTrimmed(rows) };
};

// Fallback for clipboards with no HTML flavour: a terminal, a .csv opened in a
// plain editor, Notepad. Gated on tabs — splitting on spaces would mangle prose.
export const parseDelimitedTable = (text) => {
	const lines = text.replace(/\r\n?/g, '\n').split('\n');
	while (lines.length && !lines[lines.length - 1].trim()) lines.pop();

	if (lines.length < 2 || !lines.every((line) => line.includes('\t'))) return null;

	const rows = trimEmptyTableEdges(
		lines.map((line) => line.split('\t').map((cell) => cell.trim()))
	);
	if (isTooSmallToBeATable(rows)) return null;

	return { table: buildTable(rows, false), trimmed: wasTrimmed(rows) };
};

const emptyCell = (header = false) => ({
	type: 'table-cell',
	...(header ? { header: true } : {}),
	children: [{ text: '' }]
});

const isTableCell = (n) =>
	!Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'table-cell';

const getTableCell = (editor) => {
	const { selection } = editor;
	if (!selection) return null;
	const cells = Array.from(Editor.nodes(editor, { match: isTableCell }));
	return cells.length === 1 ? cells[0] : null;
};

export const insertTableRow = (editor, { after = true } = {}) => {
	const cell = getTableCell(editor);
	if (!cell) return;
	const [, cellPath] = cell;
	const rowPath = cellPath.slice(0, -1);
	const tablePath = cellPath.slice(0, -2);
	const table = Node.get(editor, tablePath);
	const colCount = table.children[0].children.length;
	const rowIndex = rowPath[rowPath.length - 1];
	const at = [...tablePath, after ? rowIndex + 1 : rowIndex];
	Transforms.insertNodes(
		editor,
		{
			type: 'table-row',
			children: Array.from({ length: colCount }, () => emptyCell())
		},
		{ at }
	);
	Transforms.select(editor, Editor.start(editor, [...at, 0]));
};

export const insertTableColumn = (editor, { after = true } = {}) => {
	const cell = getTableCell(editor);
	if (!cell) return;
	const [, cellPath] = cell;
	const colIndex = cellPath[cellPath.length - 1];
	const rowIndex = cellPath[cellPath.length - 2];
	const tablePath = cellPath.slice(0, -2);
	const table = Node.get(editor, tablePath);
	const insertAt = after ? colIndex + 1 : colIndex;

	table.children.forEach((row, rowAt) => {
		Transforms.insertNodes(editor, emptyCell(!!row.children[colIndex]?.header), {
			at: [...tablePath, rowAt, insertAt]
		});
	});
	Transforms.select(editor, Editor.start(editor, [...tablePath, rowIndex, insertAt]));
};

export const deleteTable = (editor) => {
	const cell = getTableCell(editor);
	if (!cell) return;
	Transforms.removeNodes(editor, { at: cell[1].slice(0, -2) });
};

export const deleteTableRow = (editor) => {
	const cell = getTableCell(editor);
	if (!cell) return;
	const [, cellPath] = cell;
	const tablePath = cellPath.slice(0, -2);
	const table = Node.get(editor, tablePath);
	if (table.children.length === 1) {
		Transforms.removeNodes(editor, { at: tablePath });
		return;
	}
	Transforms.removeNodes(editor, { at: cellPath.slice(0, -1) });
};

export const deleteTableColumn = (editor) => {
	const cell = getTableCell(editor);
	if (!cell) return;
	const [, cellPath] = cell;
	const colIndex = cellPath[cellPath.length - 1];
	const tablePath = cellPath.slice(0, -2);
	const table = Node.get(editor, tablePath);
	if (table.children[0].children.length === 1) {
		Transforms.removeNodes(editor, { at: tablePath });
		return;
	}
	for (let rowAt = table.children.length - 1; rowAt >= 0; rowAt -= 1) {
		Transforms.removeNodes(editor, { at: [...tablePath, rowAt, colIndex] });
	}
};

export const moveToAdjacentTableCell = (editor, direction) => {
	const cell = getTableCell(editor);
	if (!cell) return;
	const [, cellPath] = cell;
	const tablePath = cellPath.slice(0, -2);
	const table = Node.get(editor, tablePath);
	const rowIndex = cellPath[cellPath.length - 2];
	const colIndex = cellPath[cellPath.length - 1];
	const rowCount = table.children.length;
	const colCount = table.children[0].children.length;

	if (direction === 'next') {
		if (colIndex + 1 < colCount) {
			Transforms.select(
				editor,
				Editor.start(editor, [...tablePath, rowIndex, colIndex + 1])
			);
			return;
		}
		if (rowIndex + 1 < rowCount) {
			Transforms.select(editor, Editor.start(editor, [...tablePath, rowIndex + 1, 0]));
			return;
		}
		insertTableRow(editor, { after: true });
		Transforms.select(editor, Editor.start(editor, [...tablePath, rowIndex + 1, 0]));
		return;
	}

	if (colIndex > 0) {
		Transforms.select(editor, Editor.start(editor, [...tablePath, rowIndex, colIndex - 1]));
		return;
	}
	if (rowIndex > 0) {
		Transforms.select(
			editor,
			Editor.start(editor, [...tablePath, rowIndex - 1, colCount - 1])
		);
	}
};

export const withTables = (editor, onTrimRef) => {
	const { insertData, deleteBackward, deleteForward, insertBreak } = editor;

	// eslint-disable-next-line
	editor.insertData = (data) => {
		const html = data.getData('text/html');

		// Content copied from this editor round-trips through Slate's own handler.
		// Inside a cell, fall through too — a nested table is never what's wanted.
		if ((!html || !html.includes('data-slate-fragment')) && !getTableCell(editor)) {
			const result =
				(html && parseHtmlTable(html)) || parseDelimitedTable(data.getData('text/plain'));

			if (result?.table) {
				Transforms.insertNodes(editor, [
					result.table,
					{ type: 'paragraph', children: [{ text: '' }] }
				]);
				if (result.trimmed) onTrimRef?.current?.();
				return;
			}
		}

		insertData(data);
	};

	// Without these, deleting at a cell edge merges cells into each other and
	// leaves the table structurally invalid. Expanded selection inside one cell
	// still deletes the selected text.
	// eslint-disable-next-line
	editor.deleteBackward = (unit) => {
		const cell = getTableCell(editor);
		if (
			cell &&
			Range.isCollapsed(editor.selection) &&
			Point.equals(editor.selection.anchor, Editor.start(editor, cell[1]))
		) {
			return;
		}
		deleteBackward(unit);
	};

	// eslint-disable-next-line
	editor.deleteForward = (unit) => {
		const cell = getTableCell(editor);
		if (
			cell &&
			Range.isCollapsed(editor.selection) &&
			Point.equals(editor.selection.anchor, Editor.end(editor, cell[1]))
		) {
			return;
		}
		deleteForward(unit);
	};

	// eslint-disable-next-line
	editor.insertBreak = () => {
		if (getTableCell(editor)) {
			Transforms.insertText(editor, '\n');
			return;
		}
		insertBreak();
	};

	return editor;
};

function TableToolbar({ classes }) {
	const editor = useSlate();
	if (!isBlockActive(editor, 'table-cell')) return null;

	const run = (fn) => (event) => {
		event.preventDefault();
		fn(editor);
	};

	return (
		<div className={classes.tableToolbar}>
			<div
				className={classes.tableBtn}
				title="Insert row below"
				onMouseDown={run(() => insertTableRow(editor, { after: true }))}
			>
				+ Row
			</div>
			<div
				className={classes.tableBtn}
				title="Delete this row"
				onMouseDown={run(deleteTableRow)}
			>
				− Row
			</div>
			<div
				className={classes.tableBtn}
				title="Insert column to the right"
				onMouseDown={run(() => insertTableColumn(editor, { after: true }))}
			>
				+ Col
			</div>
			<div
				className={classes.tableBtn}
				title="Delete this column"
				onMouseDown={run(deleteTableColumn)}
			>
				− Col
			</div>
			<div
				className={classes.tableBtn}
				title="Delete table"
				onMouseDown={run(deleteTable)}
			>
				Delete table
			</div>
		</div>
	);
}

function MessageEditor({ value, setValue, onUploadImage }) {
	const classes = useStyles();
	const renderElement = useCallback((props) => <Element {...props} />, []);
	const renderLeaf = useCallback((props) => <Leaf {...props} />, []);
	const dispatch = useDispatch();
	const uploadRef = useRef(onUploadImage);
	uploadRef.current = onUploadImage;
	const onTrimRef = useRef();
	onTrimRef.current = () =>
		dispatch(
			showToast({
				body: `Pasted table trimmed to ${MAX_TABLE_COLUMNS} columns and ${MAX_TABLE_ROWS} rows.`,
				type: 'warning'
			})
		);
	// withTables sits outermost so a pasted table wins over the image/link handlers.
	const editor = useMemo(
		() => withTables(withImages(withLinks(withReact(createEditor())), uploadRef), onTrimRef),
		[]
	);
	const [activeColor, setActiveColor] = useState('#000');

	const editorKeyDown = (event) => {
		if (event.key === 'Tab' && getTableCell(editor)) {
			event.preventDefault();
			moveToAdjacentTableCell(editor, event.shiftKey ? 'prev' : 'next');
			return;
		}

		// eslint-disable-next-line no-restricted-syntax
		for (const hotkey in HOTKEYS) {
			if (isHotkey(hotkey, event)) {
				event.preventDefault();
				const mark = HOTKEYS[hotkey];
				toggleMark(editor, mark);
			}
		}
	};

	return (
		<Slate
			editor={editor}
			value={value}
			onChange={(newValue) => setValue(newValue)}
		>
			<div className={classes.toolbar}>
				<InsertImageButton onUploadImage={onUploadImage} />
				<MarkButton
					classes={[classes.btn]}
					activeStyle={classes.btnActive}
					format="bold"
					icon={<FormatBoldIcon fontSize="small" />}
				/>
				<MarkButton
					classes={[classes.btn]}
					activeStyle={classes.btnActive}
					format="italic"
					icon={<FormatItalicIcon fontSize="small" />}
				/>
				<MarkButton
					classes={[classes.btn]}
					activeStyle={classes.btnActive}
					format="underline"
					icon={<FormatUnderlinedIcon fontSize="small" />}
				/>
				<BlockButton
					classes={[classes.btn]}
					activeStyle={classes.btnActive}
					format="heading-one"
					icon={<Filter1Icon fontSize="small" />}
				/>
				<BlockButton
					classes={[classes.btn]}
					activeStyle={classes.btnActive}
					format="heading-two"
					icon={<Filter2Icon fontSize="small" />}
				/>
				<LinkButton
					classes={[classes.btn]}
					activeStyle={classes.btnActive}
					type="wrap"
					icon={<LinkIcon fontSize="small" />}
				/>
				<LinkButton
					classes={[classes.btn]}
					activeStyle={classes.btnActive}
					type="unwrap"
					icon={<LinkOffIcon fontSize="small" />}
				/>
				<BlockButton
					classes={[classes.btn]}
					activeStyle={classes.btnActive}
					format="numbered-list"
					icon={<FormatListNumberedIcon fontSize="small" />}
				/>
				<BlockButton
					classes={[classes.btn]}
					activeStyle={classes.btnActive}
					format="bulleted-list"
					icon={<FormatListBulletedIcon fontSize="small" />}
				/>
				<div className={classes.wrapperColors}>
					<div
						className={classes.colorBlock}
						style={{ backgroundColor: activeColor }}
					/>
					<div className={classes.colorList}>
						{colors.map((item) => (
							<ColorButton
								key={item.type}
								classes={[classes.bulletWrap]}
								format={item.color}
								icon={
									<StatusBullet
										color={item.type}
										size="medium"
										className={classes.noMargins}
									/>
								}
								callback={(color) => setActiveColor(color)}
							/>
						))}
					</div>
				</div>
				<TableToolbar classes={classes} />
			</div>
			<div className={classes.editorWrap}>
				<Editable
					{...{ spellCheck: true }}
					renderElement={renderElement}
					renderLeaf={renderLeaf}
					autoFocus
					placeholder="Type message here..."
					onKeyDown={(event) => editorKeyDown(event)}
					className={classes.editor}
				/>
			</div>
		</Slate>
	);
}

TableToolbar.propTypes = {
	classes: PropTypes.object
};

MessageEditor.propTypes = {
	value: PropTypes.array,
	setValue: PropTypes.func,
	onUploadImage: PropTypes.func
};

ColorButton.propTypes = {
	format: PropTypes.string,
	icon: PropTypes.node,
	classes: PropTypes.array,
	callback: PropTypes.func
};

MarkButton.propTypes = {
	format: PropTypes.string,
	icon: PropTypes.node,
	classes: PropTypes.array,
	activeStyle: PropTypes.string
};

BlockButton.propTypes = {
	format: PropTypes.string,
	icon: PropTypes.node,
	classes: PropTypes.array,
	activeStyle: PropTypes.string
};

InsertImageButton.propTypes = {
	onUploadImage: PropTypes.func
};

LinkButton.propTypes = {
	type: PropTypes.string,
	icon: PropTypes.node,
	classes: PropTypes.array,
	activeStyle: PropTypes.string
};

Leaf.propTypes = {
	attributes: PropTypes.object,
	children: PropTypes.node,
	leaf: PropTypes.object
};

Element.propTypes = {
	attributes: PropTypes.object,
	children: PropTypes.node,
	element: PropTypes.object
};

Image.propTypes = {
	attributes: PropTypes.object,
	children: PropTypes.node,
	element: PropTypes.object
};

export default MessageEditor;
